import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useId, useRef, useState, type RefObject } from 'react';
import { api, ApiError, errorMessage } from '@/lib/api';
import { useIsAdmin, useMe, useTeam } from '@/lib/hooks';
import { toast, toastError } from '@/lib/toast';
import { Icon } from '@/ui/Icon';
import { Callout, Empty, Skeleton, Spinner } from '@/ui/bits';
import { findMentions, firstName } from '@shared/record';
import type { CommentItem, EntityType } from '@shared/types';
import { MentionInput } from './MentionInput';
import { keys, useComments } from './queries';
import { cardTime, dayHeading, MentionText, shake } from './util';

// Internal comments with @mentions on a person, company or deal. The record sheet shows them under its Comments
// tab with its own composer; the deals area shows them inside a deal through the "comments" slot.

/** Posts a comment, showing it straight away and taking it back if the server refuses it. */
export function usePostComment(entityType: EntityType, entityId: string) {
  const qc = useQueryClient();
  const me = useMe();
  const { members } = useTeam();
  return async (body: string): Promise<CommentItem> => {
    const key = keys.comments(entityType, entityId);
    const mentions = findMentions(body, members);
    const temp: CommentItem = {
      id: `tmp-${crypto.randomUUID()}`,
      entityType,
      entityId,
      body,
      mentions,
      author: { id: me.user.id, name: me.user.name, hue: me.user.hue },
      createdAt: new Date().toISOString(),
      editedAt: null,
    };
    await qc.cancelQueries({ queryKey: key });
    qc.setQueryData<CommentItem[]>(key, (list) => (list ? [...list, temp] : list));
    try {
      const saved = await api.post<CommentItem>('/comments', { entityType, entityId, body, mentions });
      qc.setQueryData<CommentItem[]>(key, (list) => list?.map((c) => (c.id === temp.id ? saved : c)));
      return saved;
    } catch (err) {
      qc.setQueryData<CommentItem[]>(key, (list) => list?.filter((c) => c.id !== temp.id));
      throw err;
    }
  };
}

/** "Comment posted · Tomás was notified", with an undo that takes the comment back. */
export function announceComment(comment: CommentItem, team: { id: string; name: string }[], meId: string, onUndo?: () => void): void {
  const notified = comment.mentions.filter((id) => id !== meId).map((id) => team.find((m) => m.id === id)?.name ?? '');
  const names = notified.filter(Boolean).map(firstName);
  const suffix = names.length === 0 ? '' : names.length === 1 ? ` · ${names[0]} was notified` : ` · ${names.length} teammates were notified`;
  toast(`Comment posted${suffix}`, {
    icon: 'check-circle',
    undo: () => {
      api
        .del(`/comments/${comment.id}`)
        .then(() => onUndo?.())
        .catch((err) => toastError(err));
    },
  });
}

async function deleteComment(c: CommentItem, refresh: () => void): Promise<void> {
  try {
    const res = await api.del<{ ids: string[] }>(`/comments/${c.id}`);
    refresh();
    toast('Comment deleted', {
      icon: 'trash',
      undo: () => {
        api
          .post('/bin/restore', { type: 'comment', ids: res.ids })
          .then(refresh)
          .catch((err) => toastError(err));
      },
    });
  } catch (err) {
    toastError(err);
  }
}

function CommentEditor({ comment, onDone }: { comment: CommentItem; onDone: () => void }) {
  const qc = useQueryClient();
  const { members } = useTeam();
  const [value, setValue] = useState(comment.body);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const ref = useRef<HTMLTextAreaElement>(null);
  const errId = useId();

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.focus();
    el.setSelectionRange(el.value.length, el.value.length);
  }, []);

  const save = async () => {
    const body = value.trim();
    if (!body) {
      shake(ref.current);
      setError('Write something, or delete the comment instead.');
      return;
    }
    if (body === comment.body) {
      onDone();
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const saved = await api.patch<CommentItem>(`/comments/${comment.id}`, { body, mentions: findMentions(body, members) });
      qc.setQueryData<CommentItem[]>(keys.comments(comment.entityType, comment.entityId), (list) =>
        list?.map((c) => (c.id === saved.id ? saved : c)),
      );
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? (err.fields.body ?? err.message) : errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="rcomp cmt-edit"
      onKeyDown={(e) => {
        if (e.key === 'Escape' && !e.nativeEvent.defaultPrevented) {
          e.preventDefault();
          onDone();
        }
      }}
    >
      <MentionInput
        value={value}
        onChange={setValue}
        onSubmit={save}
        placeholder="Edit your comment"
        inputRef={ref}
        mentions
        invalid={!!error}
        describedBy={error ? errId : undefined}
      />
      {error ? (
        <p className="field-err" id={errId} role="alert">
          <Icon name="warn" className="i-xs" />
          {error}
        </p>
      ) : null}
      <div className="extras">
        <span className="hint">Esc to cancel</span>
        <span className="spacer" />
        <button type="button" className="btn btn-sm btn-ghost" onClick={onDone}>
          Cancel
        </button>
        <button type="button" className="btn btn-sm btn-primary" onClick={save} disabled={busy}>
          {busy ? <Spinner label="Saving" /> : null}
          Save
        </button>
      </div>
    </div>
  );
}

function CommentCard({ comment, onReply, focused }: { comment: CommentItem; onReply?: (name: string) => void; focused: boolean }) {
  const qc = useQueryClient();
  const me = useMe();
  const admin = useIsAdmin();
  const [editing, setEditing] = useState(false);
  const mine = comment.author?.id === me.user.id;
  const pending = comment.id.startsWith('tmp-');
  const refresh = () => void qc.invalidateQueries({ queryKey: [comment.entityType, comment.entityId] });
  const author = comment.author ? firstName(comment.author.name) : 'A former teammate';

  return (
    <div className={['tl-item', focused ? 'is-focus' : ''].filter(Boolean).join(' ')} data-item={comment.id}>
      <span className="tl-ic comment" aria-hidden="true">
        <Icon name="comment" />
      </span>
      <div className={['card', 'tl-card', pending ? 'pending' : ''].filter(Boolean).join(' ')}>
        <div className="t1">
          <b>Comment by {mine ? 'you' : author}</b>
          <span className="who">team only{comment.editedAt ? ' · edited' : ''}</span>
          <time dateTime={comment.createdAt}>{pending ? 'Posting…' : cardTime(comment.createdAt)}</time>
        </div>
        {editing ? (
          <CommentEditor comment={comment} onDone={() => setEditing(false)} />
        ) : (
          <>
            <div className="t2 pre">
              <MentionText body={comment.body} />
            </div>
            {pending ? null : (
              <div className="t3">
                {!mine && onReply && comment.author ? (
                  <button type="button" className="btn btn-sm btn-ghost" onClick={() => onReply(comment.author!.name)}>
                    <Icon name="reply" />
                    Reply in thread
                  </button>
                ) : null}
                {mine ? (
                  <button type="button" className="btn btn-sm btn-ghost" onClick={() => setEditing(true)}>
                    <Icon name="edit" />
                    Edit
                  </button>
                ) : null}
                {mine || admin ? (
                  <button type="button" className="btn btn-sm btn-ghost" onClick={() => void deleteComment(comment, refresh)}>
                    <Icon name="trash" />
                    Delete
                  </button>
                ) : null}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/** The list of comments, newest first on a record and oldest first in the compact deal layout. */
export function CommentThread({
  entityType,
  entityId,
  compact,
  onReply,
  focusId,
}: {
  entityType: EntityType;
  entityId: string;
  compact?: boolean;
  onReply?: (name: string) => void;
  focusId?: string;
}) {
  const q = useComments(entityType, entityId);
  const focusDone = useRef(false);

  useEffect(() => {
    if (!focusId || focusDone.current || !q.data?.some((c) => c.id === focusId)) return;
    focusDone.current = true;
    requestAnimationFrame(() => document.querySelector(`[data-item="${focusId}"]`)?.scrollIntoView({ block: 'center' }));
  }, [focusId, q.data]);

  if (q.isPending) return <Skeleton rows={3} />;
  if (q.isError)
    return (
      <Callout
        tone="danger"
        title="Comments did not load"
        actions={
          <button type="button" className="btn btn-sm btn-tile" onClick={() => void q.refetch()}>
            <Icon name="refresh" />
            Try again
          </button>
        }
      >
        {errorMessage(q.error)}
      </Callout>
    );
  if (q.data.length === 0)
    return (
      <Empty icon="comment" title="No comments yet" className="cmt-empty">
        Type @ to bring someone in.
      </Empty>
    );
  const list = compact ? q.data : [...q.data].reverse();
  let lastDay = '';
  return (
    <div className={['tl', compact ? 'compact' : ''].filter(Boolean).join(' ')} aria-live="polite" aria-relevant="additions">
      {list.map((c) => {
        const day = dayHeading(c.createdAt);
        const head = !compact && day !== lastDay;
        lastDay = day;
        return (
          <div key={c.id} className="tl-group">
            {head ? <div className="tl-day">{day}</div> : null}
            <CommentCard comment={c} onReply={onReply} focused={c.id === focusId} />
          </div>
        );
      })}
    </div>
  );
}

/** The composer used by the comments slot (inside a deal). The record sheet uses its own composer with modes. */
function CommentComposer({
  entityType,
  entityId,
  inputRef,
  value,
  setValue,
}: {
  entityType: EntityType;
  entityId: string;
  inputRef: RefObject<HTMLTextAreaElement | null>;
  value: string;
  setValue: (value: string) => void;
}) {
  const post = usePostComment(entityType, entityId);
  const me = useMe();
  const { members } = useTeam();
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const errId = useId();

  const submit = async () => {
    const body = value.trim();
    if (!body) {
      shake(inputRef.current);
      inputRef.current?.focus();
      return;
    }
    setBusy(true);
    setError(null);
    setValue('');
    try {
      const saved = await post(body);
      announceComment(saved, members, me.user.id, () => void qc.invalidateQueries({ queryKey: [entityType, entityId] }));
    } catch (err) {
      setValue(body);
      setError(err instanceof ApiError ? (err.fields.body ?? err.message) : errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rcomp">
      <MentionInput
        value={value}
        onChange={(v) => {
          setValue(v);
          if (error) setError(null);
        }}
        onSubmit={submit}
        placeholder="Write a comment…"
        inputRef={inputRef}
        mentions
        invalid={!!error}
        describedBy={error ? errId : undefined}
      />
      {error ? (
        <p className="field-err" id={errId} role="alert">
          <Icon name="warn" className="i-xs" />
          {error}
        </p>
      ) : null}
      <div className="extras">
        <span className="hint">
          <Icon name="lock" />
          Only the team sees comments
        </span>
        <span className="spacer" />
        <button type="button" className="btn btn-sm btn-primary" onClick={submit} disabled={busy}>
          Post comment
        </button>
      </div>
    </div>
  );
}

/** The "comments" slot: a thread plus a box to write one, for a person, company or deal. */
export function CommentsSlot({ entityType, entityId, compact }: { entityType: EntityType; entityId: string; compact?: boolean }) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const [value, setValue] = useState('');
  const reply = (name: string) => {
    setValue(`@${firstName(name)} `);
    requestAnimationFrame(() => {
      const el = ref.current;
      el?.focus();
      el?.setSelectionRange(el.value.length, el.value.length);
    });
  };
  const composer = <CommentComposer entityType={entityType} entityId={entityId} inputRef={ref} value={value} setValue={setValue} />;
  return (
    <div className={['cmt-slot', compact ? 'compact' : ''].filter(Boolean).join(' ')}>
      {compact ? null : composer}
      <CommentThread entityType={entityType} entityId={entityId} compact={compact} onReply={reply} />
      {compact ? composer : null}
    </div>
  );
}
